import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ContactsDirectory = ({ contacts, onStartConversation, onCallContact }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');

  const roleOptions = [
    { value: 'all', label: 'All', icon: 'Users' },
    { value: 'student', label: 'Students', icon: 'GraduationCap' },
    { value: 'city-official', label: 'City Officials', icon: 'Building2' },
    { value: 'mentor', label: 'Mentors', icon: 'UserCheck' },
    { value: 'pcf-admin', label: 'PCF Admin', icon: 'Shield' }
  ];

  const filteredContacts = contacts?.filter(contact => {
    const matchesSearch = !searchQuery || 
      contact?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      contact?.email?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      contact?.organization?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    
    const matchesRole = roleFilter === 'all' || contact?.role === roleFilter;
    
    return matchesSearch && matchesRole;
  });

  const groupedContacts = filteredContacts?.reduce((groups, contact) => {
    const firstLetter = contact?.name?.[0]?.toUpperCase();
    if (!groups?.[firstLetter]) {
      groups[firstLetter] = [];
    }
    groups?.[firstLetter]?.push(contact);
    return groups;
  }, {});

  const sortedGroups = Object.keys(groupedContacts)?.sort();

  const getStatusColor = (status) => {
    const statusMap = {
      online: 'bg-success',
      away: 'bg-warning',
      busy: 'bg-error',
      offline: 'bg-muted-foreground'
    };
    return statusMap?.[status] || 'bg-muted-foreground';
  };

  const getRoleIcon = (role) => {
    const roleMap = {
      student: 'GraduationCap',
      'city-official': 'Building2',
      mentor: 'UserCheck',
      'pcf-admin': 'Shield'
    };
    return roleMap?.[role] || 'User';
  };

  const getRoleColor = (role) => {
    const colorMap = {
      student: 'text-blue-600',
      'city-official': 'text-green-600',
      mentor: 'text-purple-600',
      'pcf-admin': 'text-orange-600'
    };
    return colorMap?.[role] || 'text-muted-foreground';
  };

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <div className="p-4 border-b border-border bg-card">
        <div className="flex items-center space-x-2 mb-4">
          <Icon name="Users" size={24} className="text-primary" />
          <h2 className="text-lg font-semibold text-foreground">Contacts</h2>
          <span className="text-sm text-muted-foreground">({filteredContacts?.length})</span>
        </div>

        {/* Search */}
        <div className="mb-4">
          <Input
            type="search"
            placeholder="Search contacts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e?.target?.value)}
            className="w-full"
          />
        </div>

        {/* Role Filter */}
        <div className="flex space-x-1 bg-muted rounded-lg p-1">
          {roleOptions?.map((option) => (
            <Button
              key={option?.value}
              variant={roleFilter === option?.value ? "default" : "ghost"}
              size="sm"
              onClick={() => setRoleFilter(option?.value)}
              iconName={option?.icon}
              iconPosition="left"
              iconSize={14}
              className="flex-1 transition-micro"
            >
              {option?.label}
            </Button>
          ))}
        </div>
      </div>
      {/* Contacts List */}
      <div className="flex-1 overflow-y-auto">
        {filteredContacts?.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-center p-6">
            <Icon name="UserX" size={48} className="text-muted-foreground mb-4" />
            <h3 className="font-medium text-foreground mb-2">No contacts found</h3>
            <p className="text-sm text-muted-foreground">
              {searchQuery ? 'Try adjusting your search terms' : 'No contacts match the selected filter'}
            </p>
          </div>
        ) : (
          <div className="p-2">
            {sortedGroups?.map((letter) => (
              <div key={letter} className="mb-6">
                <div className="sticky top-0 bg-background/80 backdrop-blur-sm py-2 mb-2">
                  <h3 className="text-sm font-semibold text-primary border-b border-border pb-1">
                    {letter}
                  </h3>
                </div>
                
                <div className="space-y-1">
                  {groupedContacts?.[letter]?.map((contact) => (
                    <div
                      key={contact?.id}
                      className="flex items-center space-x-3 p-3 rounded-lg hover:bg-muted transition-micro"
                    >
                      {/* Avatar */}
                      <div className="relative flex-shrink-0">
                        {contact?.avatar ? (
                          <Image
                            src={contact?.avatar}
                            alt={contact?.name}
                            className="w-12 h-12 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
                            <Icon name="User" size={20} color="white" />
                          </div>
                        )}
                        <div className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-background ${getStatusColor(contact?.status)}`} />
                      </div>

                      {/* Contact Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-1">
                          <h4 className="font-medium text-foreground truncate">{contact?.name}</h4>
                          <Icon 
                            name={getRoleIcon(contact?.role)} 
                            size={14} 
                            className={getRoleColor(contact?.role)} 
                          />
                        </div>
                        <p className="text-sm text-muted-foreground truncate">{contact?.email}</p>
                        {contact?.organization && (
                          <p className="text-xs text-muted-foreground truncate">{contact?.organization}</p>
                        )}
                        <div className="flex items-center space-x-2 mt-1">
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            contact?.status === 'online' ? 'bg-success/10 text-success' :
                            contact?.status === 'away' ? 'bg-warning/10 text-warning' :
                            contact?.status === 'busy'? 'bg-error/10 text-error' : 'bg-muted text-muted-foreground'
                          }`}>
                            {contact?.status?.charAt(0)?.toUpperCase() + contact?.status?.slice(1)}
                          </span>
                          {contact?.lastSeen && contact?.status === 'offline' && (
                            <span className="text-xs text-muted-foreground">
                              Last seen {contact?.lastSeen}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center space-x-1 flex-shrink-0">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onStartConversation(contact)}
                          className="h-8 w-8"
                        >
                          <Icon name="MessageSquare" size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onCallContact(contact)}
                          className="h-8 w-8"
                          disabled={contact?.status === 'offline'}
                        >
                          <Icon name="Phone" size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                        >
                          <Icon name="MoreVertical" size={16} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ContactsDirectory;